/**
 * KpiTile — value vs target with health state. GREEN = healthy (status), per §1.
 * Health is computed per-metric because "good" direction differs (lower-is-better for
 * time/foodcost, higher-is-better for turnover).
 */
import type { KpiValue } from "@/types/contract";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import type { BadgeTone } from "@/components/ui/Badge";
import { TrendUpIcon, TrendDownIcon, CheckIcon } from "@/components/ui/icons";
import { formatSeconds } from "@/lib/utils";

interface KpiMeta {
  label: string;
  /** "lower" = меньше — лучше; "higher" = больше — лучше. */
  goodDirection: "lower" | "higher";
  format: (v: number) => string;
  unitLabel: string;
}

export const KPI_META: Record<string, KpiMeta> = {
  median_time_to_guest: {
    label: "Медиана до гостя",
    goodDirection: "lower",
    format: (v) => formatSeconds(v),
    unitLabel: "мин:сек",
  },
  table_turnover: {
    label: "Оборачиваемость столов",
    goodDirection: "higher",
    format: (v) => v.toFixed(1),
    unitLabel: "гостей/час",
  },
  // Реальная оборачиваемость (визит ≠ чек) — из visit_duration_sec.
  real_turnover: {
    label: "Реальная оборачиваемость",
    goodDirection: "higher",
    format: (v) => v.toFixed(1),
    unitLabel: "визитов/час",
  },
  median_visit_min: {
    label: "Медиана визита",
    goodDirection: "higher", // информативный: без камер = 0 (нет данных)
    format: (v) => v.toFixed(0),
    unitLabel: "мин",
  },
  foodcost_dev: {
    label: "Отклонение фудкоста",
    goodDirection: "lower",
    format: (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`,
    unitLabel: "%",
  },
  // KPI потока гостей (§4.4) — из vision_event. Короче очередь — лучше.
  avg_queue_len: {
    label: "Средняя очередь",
    goodDirection: "lower",
    format: (v) => v.toFixed(1),
    unitLabel: "чел.",
  },
  peak_queue_len: {
    label: "Пиковая очередь",
    goodDirection: "lower",
    format: (v) => Math.round(v).toString(),
    unitLabel: "чел.",
  },
  avg_occupancy: {
    label: "Средняя заполненность зон",
    goodDirection: "higher",
    format: (v) => v.toFixed(1),
    unitLabel: "чел.",
  },
  // POS-KPI из живой БД rover (без камер).
  revenue: {
    label: "Выручка",
    goodDirection: "higher",
    format: (v) => Math.round(v).toLocaleString("ru-RU"),
    unitLabel: "₸",
  },
  avg_check: {
    label: "Средний чек",
    goodDirection: "higher",
    format: (v) => Math.round(v).toLocaleString("ru-RU"),
    unitLabel: "₸",
  },
  orders: {
    label: "Заказов",
    goodDirection: "higher",
    format: (v) => Math.round(v).toLocaleString("ru-RU"),
    unitLabel: "",
  },
  orders_per_hour: {
    label: "Заказов в час",
    goodDirection: "higher",
    format: (v) => v.toFixed(1),
    unitLabel: "",
  },
  voids_after_kitchen: {
    label: "Отмены после кухни",
    goodDirection: "lower",
    format: (v) => Math.round(v).toLocaleString("ru-RU"),
    unitLabel: "₸",
  },
};

function health(kpi: KpiValue, meta: KpiMeta): {
  tone: BadgeTone;
  label: string;
} {
  const onTarget =
    meta.goodDirection === "lower"
      ? kpi.value <= kpi.target
      : kpi.value >= kpi.target;
  if (onTarget) return { tone: "accent", label: "В норме" };
  // How far off?
  const gap = Math.abs(kpi.value - kpi.target);
  const rel = kpi.target !== 0 ? gap / Math.abs(kpi.target) : gap;
  return rel > 0.25
    ? { tone: "danger", label: "Вне нормы" }
    : { tone: "warning", label: "Близко к норме" };
}

export function KpiTile({ kpi }: { kpi: KpiValue }) {
  const meta =
    KPI_META[kpi.metric] ??
    ({
      label: kpi.metric,
      goodDirection: "higher",
      format: (v: number) => String(v),
      unitLabel: kpi.unit,
    } satisfies KpiMeta);

  const h = health(kpi, meta);
  const towardGood =
    meta.goodDirection === "lower" ? kpi.value <= kpi.target : kpi.value >= kpi.target;
  const Trend = towardGood ? CheckIcon : meta.goodDirection === "lower" ? TrendUpIcon : TrendDownIcon;

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-muted">{meta.label}</h3>
        <Badge tone={h.tone}>
          <Trend size={12} />
          {h.label}
        </Badge>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="tabular text-4xl font-semibold text-ink">
          {meta.format(kpi.value)}
        </span>
        <span className="text-sm text-muted">{meta.unitLabel}</span>
      </div>

      <div className="rule" />

      <div className="tabular flex items-center justify-between text-xs text-muted">
        <span>Цель: {meta.format(kpi.target)} {meta.unitLabel}</span>
        <span>
          {kpi.period_from} — {kpi.period_to}
        </span>
      </div>
    </Card>
  );
}
